import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import { GenericHeightWeightQuestionFieldAnswers } from '../../../Generic/HeightWeight/question';

const trtEnhancedFollowupQuestions = [
  {
    field: 'trtEnhancedFollowup_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    },
    extras: { sendToBeluga: false }
  },
  {
    field: 'trtEnhancedFollowup_height',
    label: 'What is your height in feet and inches?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 1,
      name: 'trtEnhancedFollowup_height'
    }
  },
  {
    field: 'trtEnhancedFollowup_weight',
    label: 'What is your weight in pounds?',
    type: 'info',
    answer: {
      type: 'number',
      name: 'trtEnhancedFollowup_weight'
    }
  },
  {
    field: 'trtEnhancedFollowup_healthChanges',
    label:
      'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_healthChangesDetails',
    label: 'Please tell us more about the changes in your health',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'trtEnhancedFollowup_healthChangesDetails'
    }
  },
  {
    field: 'trtEnhancedFollowup_urgentSymptoms',
    label: 'In the past 72 hours, have you experienced any of the following:',
    educationalSlide: `- Chest pain or shortness of breath
- Severe dizziness or fainting
- Facial swelling, rash, or hives
- Thoughts of self-harm or feeling unsafe`,
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_emergencyCare',
    label:
      'These are concerning symptoms that should prompt immediate medical evaluation. Please contact EMS services immediately for further evaluation.',
    type: 'info',
    reject: true,
    isLast: true,
    answer: {},
    extras: { sendToBeluga: false }
  },
  {
    field: 'trtEnhancedFollowup_medicationDoing',
    label: 'How are you doing on the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Well', key: 'well' },
        { value: 'Not well', key: 'not_well' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_medicationExperience',
    label: 'Please tell us more about what you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'trtEnhancedFollowup_medicationExperience'
    }
  },
  {
    field: 'trtEnhancedFollowup_sideEffects',
    label: 'Have you experienced any side effects from the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_sideEffectsDetails',
    label: 'Please tell us more about the side effects you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'trtEnhancedFollowup_sideEffectsDetails'
    }
  },
  {
    field: 'trtEnhancedFollowup_testicularSizeDecrease',
    label: 'Are you noticing a decrease in testicular size or fullness?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_hcgInterest',
    label:
      'You noted a decrease in testicular size or fullness, which can occur during testosterone replacement therapy. One option to address this is adding human chorionic gonadotropin (hCG), which helps stimulate the testes to produce testosterone and can support testicular size and function. This would involve regular injections and continued ongoing monitoring to assess response and ensure appropriate dosing. Please review the educational material about this medication in the consent for further information, and feel free to ask any questions at the end of this questionnaire.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'I would like to add HCG', key: 'add_hcg' },
        { value: "No, I'm not interested in HCG", key: 'no_hcg' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_lastDoseDate',
    label:
      'Please enter the exact date you last administered your testosterone dose. Accurate timing helps us safely determine your next scheduled dose.',
    type: 'info',
    answer: {
      type: 'text',
      rows: 2,
      name: 'trtEnhancedFollowup_lastDoseDate'
    }
  },
  {
    field: 'trtEnhancedFollowup_doseAdjusted',
    label:
      'Has your dose been adjusted by another provider or have you self adjusted your dose since your last visit?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes, this has been adjusted', key: 'yes_adjusted' },
        {
          value:
            "No, I'm taking the same dose that was prescribed during my last appointment",
          key: 'no_same_dose'
        }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_doseAdjustedDetails',
    label:
      'Please provide more information about who adjusted your dose, why this was adjusted, and what is your current dose?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'trtEnhancedFollowup_doseAdjustedDetails'
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamLibido',
    label: 'Since your last visit, how would you rate your libido (sex drive)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Terrible', key: 'terrible_1' },
        { value: 'Poor', key: 'poor_2' },
        { value: 'Average', key: 'average_3' },
        { value: 'Good', key: 'good_4' },
        { value: 'Excellent', key: 'excellent_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamEnergy',
    label: 'Since your last visit, how would you rate your energy level?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Terrible', key: 'terrible_1' },
        { value: 'Poor', key: 'poor_2' },
        { value: 'Average', key: 'average_3' },
        { value: 'Good', key: 'good_4' },
        { value: 'Excellent', key: 'excellent_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamStrengthEndurance',
    label:
      'Since your last visit, how would you rate your strength or endurance?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Terrible', key: 'terrible_1' },
        { value: 'Poor', key: 'poor_2' },
        { value: 'Average', key: 'average_3' },
        { value: 'Good', key: 'good_4' },
        { value: 'Excellent', key: 'excellent_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamEnjoymentOfLife',
    label: 'Since your last visit, how would you rate your enjoyment of life?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Terrible', key: 'terrible_1' },
        { value: 'Poor', key: 'poor_2' },
        { value: 'Average', key: 'average_3' },
        { value: 'Good', key: 'good_4' },
        { value: 'Excellent', key: 'excellent_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamMood',
    label:
      'Since your last visit, how would you rate your overall happiness or mood?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Terrible', key: 'terrible_1' },
        { value: 'Poor', key: 'poor_2' },
        { value: 'Average', key: 'average_3' },
        { value: 'Good', key: 'good_4' },
        { value: 'Excellent', key: 'excellent_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamErections',
    label: 'Since your last visit, how strong are your erections?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Extremely weak', key: 'extremely_weak_1' },
        { value: 'Weak', key: 'weak_2' },
        { value: 'Moderate', key: 'moderate_3' },
        { value: 'Strong', key: 'strong_4' },
        { value: 'Extremely strong', key: 'extremely_strong_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamWorkPerformance',
    label:
      'Since your last visit, how would you rate your work performance over the past 4 weeks?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Terrible', key: 'terrible_1' },
        { value: 'Poor', key: 'poor_2' },
        { value: 'Average', key: 'average_3' },
        { value: 'Good', key: 'good_4' },
        { value: 'Excellent', key: 'excellent_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamSleepAfterDinner',
    label: 'Since your last visit, how often do you fall asleep after dinner?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Never', key: 'never_1' },
        { value: '1-2 times per week', key: '1_2_weekly_2' },
        { value: '3-4 times per week', key: '3_4_weekly_3' },
        { value: '5-6 times per week', key: '5_6_weekly_4' },
        { value: 'Every night', key: 'every_night_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamSportsPerformance',
    label:
      'Since your last visit, how would you rate your sports or physical performance over the past 4 weeks?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Terrible', key: 'terrible_1' },
        { value: 'Poor', key: 'poor_2' },
        { value: 'Average', key: 'average_3' },
        { value: 'Good', key: 'good_4' },
        { value: 'Excellent', key: 'excellent_5' }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_qadamHeightLost',
    label: 'How much height have you lost?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'None (0-0.4 inches)', key: 'none_zero_to_zero_four_inches_5' },
        { value: '0.5-0.9 inches', key: 'zero_five_to_zero_nine_inches_4' },
        { value: '1-1.4 inches', key: 'one_to_one_four_inches_3' },
        { value: '1.5-1.9 inches', key: 'one_five_to_one_nine_inches_2' },
        { value: '2 inches or more', key: 'two_or_more_inches_1' },
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_treatmentPreference',
    label:
      'Would you like to continue your current treatment, change the dose, or change the medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            "I'm doing well and I would like to continue what I'm on",
          key: 'continue_current'
        },
        {
          value:
            "I like the medication but I would like to increase the dose if I'm not on the max dose already",
          key: 'increase_dose'
        },
        {
          value: 'I like the medication but I would like to decrease the dose',
          key: 'decrease_dose'
        },
        {
          value: 'I would like to change the medication',
          key: 'change_medication'
        },
        {
          value: "I'm not sure, have the provider reach out to discuss further",
          key: 'provider_reach_out'
        }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_hasRecentLabResults',
    label:
      'Do you have LAB results within the last 30 days that show your testosterone levels?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    },
    extras: {
      sendToBeluga: false
    }
  },
  {
    field: 'trtEnhancedFollowup_recentLabsUpload',
    label:
      'To ensure the highest quality of care during your telemedicine consultation, we kindly request your assistance in providing an image of your initial or most recent labs obtained upon diagnosis that demonstrate your testosterone levels. Please be assured that these images will remain private and will not be shared. The physician will use the picture solely for assessment purposes to better understand your health condition. If you are unable to upload recent lab results OR if your labs were abnormal, you will be required to complete blood testing before proceeding with treatment. Thank you for your cooperation and understanding in helping to provide comprehensive care remotely.',
    type: 'file_upload',
    accepts: ['application/pdf', 'image/png', 'image/jpeg'],
    answer: [
      {
        resourceId: 'trtEnhanced_continuationInitialLabsUpload',
        buttonLabel: 'Upload Image'
      }
    ]
  },
  {
    field: 'trtEnhancedFollowup_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'trtEnhancedFollowup_additionalQuestions'
    }
  },
  {
    field: 'trtEnhancedFollowup_consent',
    label: 'TRT, HCG, and SERM-Based Therapy Informed Consent',
    type: 'select',
    educationalSlide: `<div class="!whitespace-normal">
<p>You are requesting evaluation and possible treatment for symptoms related to low testosterone. Treatment options may include:</p>
<ul>
  <li>Testosterone Replacement Therapy (TRT)</li>
  <li>Human Chorionic Gonadotropin (HCG)</li>
  <li>Selective Estrogen Receptor Modulators (SERMs) such as clomiphene or enclomiphene</li>
  <li>Aromatase inhibitors (AIs) when clinically appropriate</li>
</ul>

<p>These therapies may be used alone or in combination depending on your symptoms, laboratory results, and treatment goals (including fertility preservation).</p>

<p>TRT may be administered as an injection, gel, oral regimen, cream, or nasal formulation. HCG, SERMs, and AIs are oral or injectable therapies used to stimulate or regulate endogenous hormone production. All treatment is prescribed and monitored by a licensed healthcare provider.</p>

<h2>Potential Benefits</h2>
<p>Treatment may improve:</p>
<ul>
  <li>Energy, libido, and sexual function</li>
  <li>Muscle mass, strength, and exercise tolerance</li>
  <li>Mood and overall well-being</li>
</ul>
<p>HCG and SERMs may help stimulate natural testosterone production and support fertility in some patients.</p>

<h2>Risks and Side Effects</h2>
<p>TRT:</p>
<ul>
  <li>Acne, oily skin</li>
  <li>Fluid retention</li>
  <li>Mood changes</li>
  <li>Testicular shrinkage and reduced sperm production</li>
  <li>Elevated red blood cell count</li>
</ul>

<p>HCG:</p>
<ul>
  <li>Injection site irritation</li>
  <li>Headache or mood changes</li>
  <li>Gynecomastia</li>
  <li>Increased estrogen levels</li>
</ul>

<p>Clomiphene / Enclomiphene (SERMs):</p>
<ul>
  <li>Headache</li>
  <li>Mood changes</li>
  <li>Visual disturbances (rare but important)</li>
  <li>Possible liver or lipid changes</li>
</ul>

<p>Aromatase Inhibitors (AIs):</p>
<ul>
  <li>Joint pain</li>
  <li>Fatigue</li>
  <li>Low estrogen symptoms (e.g., mood or libido changes)</li>
  <li>Decreased bone density with long-term use</li>
</ul>

<p>Serious (rare) risks:</p>
<ul>
  <li>Elevated hematocrit</li>
  <li>Cardiovascular events (clot, stroke, heart attack)</li>
  <li>Worsening sleep apnea</li>
  <li>Liver abnormalities</li>
  <li>Allergic reactions</li>
</ul>
<p>Seek emergency care for chest pain, shortness of breath, severe headache, or leg swelling.</p>

<h2>Fertility Considerations</h2>
<ul>
  <li>TRT may suppress sperm production and lead to infertility</li>
  <li>HCG and SERMs may help preserve or stimulate fertility, but this is not guaranteed</li>
</ul>

<h2>Compounded Medications</h2>
<p>Some therapies may be compounded and not FDA-approved. These may be used when clinically appropriate; however, comparative safety and efficacy data may be limited.</p>

<h2>Contraindications</h2>
<p>You must disclose any history of:</p>
<ul>
  <li>Prostate or breast cancer</li>
  <li>Polycythemia</li>
  <li>Severe heart disease or recent stroke</li>
  <li>Untreated sleep apnea</li>
  <li>Allergy to any prescribed therapy</li>
</ul>

<h2>Monitoring Requirements</h2>
<p>Ongoing treatment requires periodic labs (typically every 3-6 months), which may include:</p>
<ul>
  <li>CBC (hematocrit)</li>
  <li>Testosterone levels</li>
  <li>PSA (when appropriate)</li>
  <li>Liver function and lipids</li>
  <li>Estradiol (when indicated)</li>
</ul>
<p>Failure to complete required monitoring may result in dose adjustment or discontinuation.</p>

<h2>Alternatives</h2>
<ul>
  <li>No treatment</li>
  <li>Lifestyle optimization (sleep, exercise, weight loss)</li>
  <li>Non-hormonal therapies</li>
  <li>HCG or SERMs alone (fertility-focused approaches)</li>
</ul>

<h2>Truthfulness and Accuracy of Information</h2>
<p>I acknowledge that the safety and appropriateness of my treatment depend on the accuracy and completeness of the information I provide. I confirm that:</p>
<ul>
  <li>All medical history, medications, allergies, and symptoms I have provided are true, accurate, and complete to the best of my knowledge</li>
  <li>I will update my provider if there are any changes to my health, medications, or symptoms</li>
  <li>I understand that failure to provide accurate or complete information may increase my risk of adverse outcomes and may result in discontinuation of care</li>
</ul>

<h2>Acknowledgment and Consent</h2>
<p>I understand:</p>
<ul>
  <li>The risks, benefits, and alternatives of TRT, HCG, SERMs, and AIs</li>
  <li>No outcomes are guaranteed</li>
  <li>Ongoing monitoring is required</li>
  <li>My treatment may be modified or discontinued for safety</li>
</ul>

<p>I consent to evaluation and treatment under medical supervision.</p>
</div>`,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do NOT wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'trtEnhancedFollowup_complete',
    isLast: true,
    label:
      'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'trtEnhancedFollowup_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    reject: true,
    answer: {},
    type: 'info',
    isLast: true
  }
] as const;

export type TrtEnhancedFollowupQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof trtEnhancedFollowupQuestions>
> &
  GenericHeightWeightQuestionFieldAnswers & {
    trtEnhanced_continuationInitialLabsUpload?: string;
  };

export default trtEnhancedFollowupQuestions;
